import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { SalePreviewModal } from './Sales';
import { useNavigate } from 'react-router-dom';
import { canAccessPage } from '../utils/pages';
import { HeaderSearch, StatChips } from '../components/HeaderControls';
import RecordSalePaymentModal from '../components/RecordSalePaymentModal';
import { fmtMoney, fmtCompact } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';
import type { SaleInvoice, Entity } from '../types';
import { invoiceNoMatches, shortInvoiceNo } from '../utils/invoiceNo';
import { fmtInInvoiceCurrency } from '../utils/invoiceCurrency';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { Paging } from '../components/ui/Paging';

// ── Helpers ───────────────────────────────────────────────────────────────────
// This page mirrors AP.tsx's structure exactly, but for money customers owe
// us (Accounts Receivable) instead of money we owe suppliers (Accounts
// Payable) — same shape, opposite direction. Kept as its own page rather than
// merged into AP.tsx so the existing, working AP flow isn't touched.

function daysFromToday(dateStr: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(dateStr + 'T00:00:00');
  return Math.floor((today.getTime() - due.getTime()) / 86_400_000);
}
function outstanding(inv: SaleInvoice): number {
  // creditedUSD = sales returns / credit notes against this sale (Phase B2)
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}
// Invoice No is stored in full, but shown here as just the prefix + last 4
// digits to keep the column compact. Keeping the prefix matters — a bare
// last-4 truncation collapses different prefixes into the same number.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
// Every broker on an invoice: the old invoice-level one plus each line's
// Broker 1 / 2, once each (same rule as the Sales list's Broker column).
function invoiceBrokerIds(inv: { brokerEntityId?: string; lineItems?: { broker1EntityId?: string; broker2EntityId?: string }[] }): string[] {
  const ids = [inv.brokerEntityId, ...(inv.lineItems ?? []).flatMap(li => [li.broker1EntityId, li.broker2EntityId])];
  return [...new Set(ids.filter((x): x is string => !!x))];
}

function todayISO() {
  const d = new Date();   // local date, not UTC (see CLAUDE.md: Date-only strings)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The Due Date column filter groups invoices by how urgent they are.
const DUE_ORDER = ['Overdue', 'Due this week', 'Later', 'Paid'];
function dueBucket(inv: SaleInvoice): string {
  if (inv.paymentStatus === 'paid') return 'Paid';
  const days = daysFromToday(inv.dueDate);
  return days > 0 ? 'Overdue' : days >= -7 ? 'Due this week' : 'Later';
}
function statusLabel(inv: SaleInvoice): string {
  return inv.paymentStatus === 'paid' ? 'Paid' : inv.paymentStatus === 'partial' ? 'Partly paid' : 'Unpaid';
}
// The list opens on what's still owed; Paid is one tick away in the Status filter.
const DEFAULT_STATUS = ['Unpaid', 'Partly paid'];
function onDefaultFilters(selected: Record<string, string[]>): boolean {
  const keys = Object.keys(selected);
  return keys.length === 1 && keys[0] === 'status'
    && selected.status.length === DEFAULT_STATUS.length && DEFAULT_STATUS.every(v => selected.status.includes(v));
}

function buildCalDays(year: number, month: number): (Date | null)[] {
  const first  = new Date(year, month, 1);
  const days: (Date | null)[] = [];
  for (let i = 0; i < first.getDay(); i++) days.push(null);
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];


// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconList() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5h10M2 7h10M2 10.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
function IconCalendar() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="2.5" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCheckCircle({ size = 32 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2" />
    <path d="M10.5 16.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── PayBadge ──────────────────────────────────────────────────────────────────

function PayBadge({ inv }: { inv: SaleInvoice }) {
  const status = inv.paymentStatus ?? 'unpaid';
  const paid   = inv.paidUSD ?? 0;
  const pct    = inv.totalAmountUSD > 0 ? Math.round((paid / inv.totalAmountUSD) * 100) : 0;
  if (status === 'paid') return (
    <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)' }}>Paid</span>
  );
  if (status === 'partial') return (
    <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)' }}>{pct}% paid</span>
  );
  return (
    <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>Unpaid</span>
  );
}

// ── Invoice detail popup ──────────────────────────────────────────────────────
// The same sale popup as the Sales page (Sales.tsx SalePreviewModal), opened
// on its Payments tab: the money timeline, Edit / Delete a payment and the
// due dates all live there now, so nothing is shown twice.

function InvoiceDetailModal({
  invoice, onClose, onPay, entities,
}: {
  invoice: SaleInvoice | null;
  onClose: () => void;
  onPay:   (inv: SaleInvoice) => void;
  entities: Entity[];
}) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const canOpenSales = canAccessPage(me, 'sales');
  return (
    <SalePreviewModal
      invoice={invoice}
      entityName={id => entities.find(e => e.id === id)?.companyName ?? '—'}
      onClose={onClose}
      onPay={onPay}
      onView={canOpenSales ? (id => navigate(`/sales?view=${id}`)) : undefined}
      initialTab="payments"
    />
  );
}

// ── Calendar view ─────────────────────────────────────────────────────────────

function CalendarView({
  invoices, entities, calYear, calMonth,
  onPrevMonth, onNextMonth, onSelect,
}: {
  invoices:    SaleInvoice[];
  entities:    Entity[];
  calYear:     number;
  calMonth:    number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelect:    (inv: SaleInvoice) => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const moneyCompact = (v: number) => fmtCompact(v, settings.currency);
  const today    = todayISO();
  const days     = buildCalDays(calYear, calMonth);
  const now      = new Date();
  const isFuture = calYear > now.getFullYear() ||
    (calYear === now.getFullYear() && calMonth > now.getMonth());

  const byDate = useMemo(() => {
    const map: Record<string, SaleInvoice[]> = {};
    invoices.forEach(inv => {
      const d = new Date(inv.dueDate + 'T00:00:00');
      if (d.getFullYear() === calYear && d.getMonth() === calMonth) {
        if (!map[inv.dueDate]) map[inv.dueDate] = [];
        map[inv.dueDate].push(inv);
      }
    });
    return map;
  }, [invoices, calYear, calMonth]);

  function entityName(id: string) {
    return entities.find(e => e.id === id)?.companyName ?? '—';
  }

  const monthTotal = useMemo(() =>
    Object.values(byDate).flat().reduce((s, inv) => s + outstanding(inv), 0),
  [byDate]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Calendar header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'var(--surface)', border: '1.5px solid var(--border)',
          borderRadius: 'var(--r-sm)', overflow: 'hidden', boxShadow: 'var(--shadow-xs)',
        }}>
          <button
            onClick={onPrevMonth}
            style={{ padding: '6px 11px', border: 'none', borderRight: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontSize: 16, lineHeight: 1, transition: 'background 0.12s', fontFamily: 'inherit' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >‹</button>
          <span style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13, width: 150, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', color: 'var(--text-1)', userSelect: 'none' }}>
            {MONTH_NAMES[calMonth]} {calYear}
          </span>
          <button
            onClick={onNextMonth}
            disabled={isFuture}
            style={{ padding: '6px 11px', border: 'none', borderLeft: '1px solid var(--border)', background: 'transparent', cursor: isFuture ? 'not-allowed' : 'pointer', color: isFuture ? 'var(--text-3)' : 'var(--text-2)', fontSize: 16, lineHeight: 1, opacity: isFuture ? 0.4 : 1, transition: 'background 0.12s', fontFamily: 'inherit' }}
            onMouseEnter={e => { if (!isFuture) e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >›</button>
        </div>

        {monthTotal > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            <span style={{ fontWeight: 700, color: 'var(--danger)', fontFamily: 'var(--font-num)' }}>{money(monthTotal)}</span>
            {' '}<span style={{ color: 'var(--text-3)' }}>({Object.values(byDate).flat().length} invoice{Object.values(byDate).flat().length !== 1 ? 's' : ''})</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {[
            { color: 'var(--danger)',  bg: '#fef2f2', border: '#fca5a5', label: 'Overdue' },
            { color: '#c2410c',        bg: '#fff7ed', border: '#fdba74', label: 'Due soon (≤7d)' },
            { color: 'var(--primary)', bg: 'var(--primary-light)', border: 'rgba(67,97,238,0.25)', label: 'Upcoming' },
          ].map(({ bg, border, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, flexShrink: 0, display: 'inline-block' }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        {DAY_NAMES.map((d, i) => (
          <div key={d} style={{
            padding: '5px 0', textAlign: 'center',
            fontSize: 10, fontWeight: 600, color: i === 0 || i === 6 ? 'var(--text-3)' : 'var(--text-2)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{d}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((day, i) => {
          if (!day) {
            return (
              <div key={`empty-${i}`} style={{
                minHeight: 64,
                borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-2)',
                borderBottom: i < days.length - 7 ? '1px solid var(--border-2)' : 'none',
                background: 'var(--surface-2)',
              }} />
            );
          }

          // Local date (toISOString put each invoice a day late ahead of UTC).
          const dStr      = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
          const isToday   = dStr === today;
          const isPast    = dStr < today;
          const dayInvs   = byDate[dStr] ?? [];
          const shown     = dayInvs.slice(0, 2);
          const overflow  = dayInvs.length - 2;
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const dayTotal  = dayInvs.reduce((s, inv) => s + outstanding(inv), 0);

          return (
            <div key={dStr} style={{
              minHeight: 64, padding: '5px 5px',
              borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-2)',
              borderBottom: i < days.length - 7 ? '1px solid var(--border-2)' : 'none',
              background: isToday
                ? 'rgba(67,97,238,0.04)'
                : isWeekend ? 'rgba(148,163,184,0.04)'
                : 'var(--surface)',
              transition: 'background 0.1s',
            }}>
              <div style={{ marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: isToday ? 'var(--primary)' : 'transparent',
                  color: isToday ? '#fff' : isPast ? 'var(--text-3)' : isWeekend ? 'var(--text-3)' : 'var(--text-2)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: isToday ? 700 : 500, flexShrink: 0,
                }}>{day.getDate()}</span>
                {dayInvs.length > 0 && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-num)',
                    color: isPast ? 'var(--danger)' : 'var(--primary)',
                    flexShrink: 0,
                  }}>{moneyCompact(dayTotal)}</span>
                )}
              </div>

              {shown.map(inv => {
                const isOverdue = inv.dueDate < today;
                const isSoon    = !isOverdue && daysFromToday(inv.dueDate) >= -7;
                const chipBg    = isOverdue ? '#fef2f2'  : isSoon ? '#fff7ed'  : 'var(--primary-light)';
                const chipColor = isOverdue ? 'var(--danger)' : isSoon ? '#c2410c' : 'var(--primary)';
                const chipBorder= isOverdue ? '#fca5a5'  : isSoon ? '#fdba74'  : 'rgba(67,97,238,0.22)';
                return (
                  <div
                    key={inv.id}
                    onClick={() => onSelect(inv)}
                    title={`${inv.invoiceNo} — ${entityName(inv.toEntityId)} — ${money(outstanding(inv))} outstanding`}
                    style={{
                      padding: '2px 4px', borderRadius: 3, marginBottom: 2,
                      background: chipBg, color: chipColor,
                      border: `1px solid ${chipBorder}`,
                      fontSize: 9.5, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3,
                      overflow: 'hidden', transition: 'opacity 0.12s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {entityName(inv.toEntityId)}
                    </span>
                    <span style={{ flexShrink: 0, opacity: 0.85 }}>
                      {moneyCompact(outstanding(inv))}
                    </span>
                  </div>
                );
              })}
              {overflow > 0 && (
                <div style={{ fontSize: 9, color: 'var(--text-3)', paddingLeft: 1, fontWeight: 500 }}>
                  +{overflow}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ARPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const now = new Date();

  const [invoices,       setInvoices]       = useState<SaleInvoice[]>([]);
  const [entities,       setEntities]       = useState<Entity[]>([]);
  const [loading,        setLoading]        = useState(true);
  // Paid invoices load too (live), so the Status column filter can show
  // them; the list opens on Unpaid + Partly paid.
  const [paidInvoices,   setPaidInvoices]   = useState<SaleInvoice[]>([]);
  const [searchQ,        setSearchQ]        = useState('');
  const [payTarget,      setPayTarget]      = useState<SaleInvoice | null>(null);
  const [viewMode,       setViewMode]       = useState<'list' | 'calendar'>('list');
  const [calYear,        setCalYear]        = useState(now.getFullYear());
  const [calMonth,       setCalMonth]       = useState(now.getMonth());
  const [detailInvoice,  setDetailInvoice]  = useState<SaleInvoice | null>(null);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;

    // Single-field `in` filter — no composite index needed.
    // dueDate sorting is done client-side.
    const u1 = onSnapshot(
      query(
        collection(db, 'sales'),
        where('paymentStatus', 'in', ['unpaid', 'partial']),
      ),
      snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice));
        rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
        setInvoices(rows);
        setLoading(false);
      },
      err => { console.error('AR invoices listener error:', err); setLoading(false); },
    );
    const u2 = onSnapshot(
      collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
      err => console.error('entities listener error:', err),
    );
    const u3 = onSnapshot(
      query(collection(db, 'sales'), where('paymentStatus', '==', 'paid')),
      snap => setPaidInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))),
      err => console.error('paid invoices listener error:', err),
    );
    return () => { u1(); u2(); u3(); };
  }, [business]);

  function entityName(id: string) {
    return entities.find(e => e.id === id)?.companyName ?? '—';
  }

  const today     = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const weekLater = useMemo(() => new Date(today.getTime() + 7 * 86_400_000), [today]);

  const totalOutstanding = useMemo(() => invoices.reduce((s, i) => s + outstanding(i), 0), [invoices]);
  const overdueInvoices  = useMemo(() => invoices.filter(i => new Date(i.dueDate + 'T00:00:00') < today), [invoices, today]);
  const overdueAmount    = useMemo(() => overdueInvoices.reduce((s, i) => s + outstanding(i), 0), [overdueInvoices]);
  const weekInvoices     = useMemo(() => invoices.filter(i => { const d = new Date(i.dueDate + 'T00:00:00'); return d >= today && d <= weekLater; }), [invoices, today, weekLater]);
  const weekAmount       = useMemo(() => weekInvoices.reduce((s, i) => s + outstanding(i), 0), [weekInvoices]);

  const baseVisible = useMemo(() => {
    const base = [...invoices, ...paidInvoices];
    const q = searchQ.trim().toLowerCase();
    if (!q) return base;
    return base.filter(inv =>
      invoiceNoMatches(inv.invoiceNo, q) ||
      entityName(inv.toEntityId).toLowerCase().includes(q)
    );
  }, [invoices, paidInvoices, searchQ, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Excel-style filters on the header (Invoice No, Customer, Due Date, Status).
  const filterColumns = useMemo<FilterColumn<SaleInvoice>[]>(() => [
    { key: 'invoice',  label: 'Invoice No', get: i => shortInvoiceNo(i.invoiceNo) },
    { key: 'customer', label: 'Customer',   get: i => entityName(i.toEntityId) },
    { key: 'broker',   label: 'Broker',     get: i => invoiceBrokerIds(i).map(id => entityName(id)) },
    { key: 'due',      label: 'Due Date',   get: i => dueBucket(i), sort: (a, b) => DUE_ORDER.indexOf(a) - DUE_ORDER.indexOf(b) },
    { key: 'status',   label: 'Status',     get: i => statusLabel(i) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: visible, filters: colFilters } = useColumnFilters(baseVisible, filterColumns, { status: DEFAULT_STATUS });
  const paging = usePaging(visible, `${searchQ}|${colFilters.stamp}`);
  const pagedInvoices = paging.pageRows;
  const listTotals = useMemo(() => visible.reduce((t, i) => ({
    amount: t.amount + i.totalAmountUSD, paid: t.paid + (i.paidUSD ?? 0), owed: t.owed + outstanding(i),
  }), { amount: 0, paid: 0, owed: 0 }), [visible]);
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [visible.length > 0, loading, viewMode]);

  function prevCalMonth() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  }
  function nextCalMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  }

  return (
    <AppLayout title="Accounts Receivable" subtitle="Outstanding payments from customers"
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Invoice no, customer…" label="Search receivables" />
        {(searchQ || !onDefaultFilters(colFilters.selected)) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); colFilters.clear(); colFilters.set('status', DEFAULT_STATUS); }}>Clear</button>
        )}
      </>}
      headerActions={
        <div className="tb-seg" role="tablist" aria-label="View">
          {(['list', 'calendar'] as const).map(mode => (
            <button key={mode} type="button" role="tab" aria-selected={viewMode === mode}
              className={viewMode === mode ? 'on' : undefined} onClick={() => setViewMode(mode)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {mode === 'list' ? <IconList /> : <IconCalendar />}
              {mode === 'list' ? 'List' : 'Calendar'}
            </button>
          ))}
        </div>
      }
    >

      {/* Figures for everything still owed (whatever the filters say). */}
      {/* Plain figures: the amount, and "None" when there's nothing.
          The invoice count is a small grey note, not mixed into the amount. */}
      <StatChips items={[
        { label: 'To collect', value: <>{money(totalOutstanding)} <span className="tb-stat-note">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span></> },
        { label: 'Overdue', tone: overdueAmount > 0 ? 'danger' : undefined, value: overdueAmount > 0
          ? <>{money(overdueAmount)} <span className="tb-stat-note">{overdueInvoices.length} invoice{overdueInvoices.length !== 1 ? 's' : ''}</span></>
          : <span className="tb-stat-none">None</span> },
        { label: 'Due this week', tone: weekAmount > 0 ? 'warning' : undefined, value: weekAmount > 0
          ? <>{money(weekAmount)} <span className="tb-stat-note">{weekInvoices.length} invoice{weekInvoices.length !== 1 ? 's' : ''}</span></>
          : <span className="tb-stat-none">None</span> },
        { label: 'Customers', value: String(new Set(invoices.map(i => i.toEntityId)).size) },
      ]} end={viewMode === 'list' ? <ColumnFilterChips filters={colFilters} inline /> : undefined} />

      {/* ── List view ── */}
      {viewMode === 'list' && (
        loading ? (
          <div className="table-wrap">
            <table><thead><tr>{[120,100,90,80,90,80,75,88].map((w,i) => (
              <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
            ))}</tr></thead></table>
          </div>
        ) : visible.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ marginBottom: 12, color: 'var(--success)', display: 'flex', justifyContent: 'center' }}><IconCheckCircle /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
              {baseVisible.length === 0 ? 'No invoices yet' : onDefaultFilters(colFilters.selected) && !searchQ ? 'All caught up!' : 'Nothing matches these filters'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
              {onDefaultFilters(colFilters.selected) && !searchQ ? 'All invoices have been paid.' : 'Try clearing a filter.'}
            </div>
          </div>
        ) : (
          <>
          <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
            <table>
              <thead>
                <tr>
                  <th className="th-filterable">Invoice No<ColumnFilterButton filters={colFilters} colKey="invoice" label="Invoice No" /></th>
                  <th className="th-filterable" style={{ width: 190, maxWidth: 190 }}>Customer<ColumnFilterButton filters={colFilters} colKey="customer" label="Customer" /></th>
                  <th className="th-filterable" style={{ width: 150 }}>Broker<ColumnFilterButton filters={colFilters} colKey="broker" label="Broker" /></th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right', minWidth: 130 }}>Paid</th><th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th>Sale Date</th>
                  <th className="th-filterable">Due Date<ColumnFilterButton filters={colFilters} colKey="due" label="Due Date" /></th>
                  <th className="th-filterable">Status<ColumnFilterButton filters={colFilters} colKey="status" label="Status" align="right" /></th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedInvoices.map(inv => {
                  const owed    = outstanding(inv);
                  const paid    = inv.paidUSD ?? 0;
                  const days    = daysFromToday(inv.dueDate);
                  const overdue = days > 0;
                  const dueSoon = !overdue && days >= -7;
                  return (
                    <tr key={inv.id} onClick={() => setDetailInvoice(inv)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span
                          title={inv.invoiceNo || undefined}
                          style={{
                            display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                            fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                          }}
                        >{lotNoDisplay(inv.invoiceNo)}</span>
                      </td>
                      <td style={{ maxWidth: 190 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span className="cell-clip" title={entityName(inv.toEntityId)} style={{ fontWeight: 500 }}>{entityName(inv.toEntityId)}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', width: 150, maxWidth: 150 }}>
                        {(() => {
                          const ids = invoiceBrokerIds(inv);
                          return ids.length
                            ? <div style={{ lineHeight: 1.35 }} title={ids.map(id => entityName(id)).join(', ')}>
                                {ids.map(id => <div key={id} className="cell-clip" style={{ maxWidth: 150 }}>{entityName(id)}</div>)}
                              </div>
                            : <span style={{ opacity: 0.4 }}>—</span>;
                        })()}
                      </td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', textAlign: 'right' }}>
                        {money(inv.totalAmountUSD)}
                        {(() => { const f = fmtInInvoiceCurrency(inv.totalAmountUSD, inv, settings.currency); return f ? <span className="inv-cur-note">{f}</span> : null; })()}
                      </td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--success)', fontWeight: 600, textAlign: 'right' }}>{paid > 0 ? money(paid) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, textAlign: 'right', color: overdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)' }}>
                        {money(owed)}
                        {(() => { const f = fmtInInvoiceCurrency(owed, inv, settings.currency); return f ? <span className="inv-cur-note">{f}</span> : null; })()}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                        {inv.saleDate
                          ? new Date(inv.saleDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span style={{ color: 'var(--text-3)', opacity: 0.5 }}>—</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: overdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)' }}>
                            {new Date(inv.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: overdue ? 'var(--danger)' : dueSoon ? '#f59e0b' : 'var(--text-3)' }}>
                            {overdue ? `+${days}d over` : days === 0 ? 'Today' : `${Math.abs(days)}d left`}
                          </span>
                        </div>
                      </td>
                      <td><PayBadge inv={inv} /></td>
                      <td className="col-actions" onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px' }}
                            onClick={() => setPayTarget(inv)}
                          >
                            <IconPay /> Pay
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                    Total · {visible.length} invoice{visible.length !== 1 ? 's' : ''}
                  </td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', textAlign: 'right' }}>{money(listTotals.amount)}</td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--success)', textAlign: 'right' }}>{money(listTotals.paid)}</td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', textAlign: 'right' }}>{money(listTotals.owed)}</td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
          <Paging page={paging.page} totalPages={paging.totalPages} total={paging.total} onChange={paging.setPage} noun="invoice" />
          </>
        )
      )}

      {/* ── Calendar view ── */}
      {viewMode === 'calendar' && (
        loading ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)' }}>
            Loading invoices…
          </div>
        ) : (
          <CalendarView
            invoices={invoices}
            entities={entities}
            calYear={calYear}
            calMonth={calMonth}
            onPrevMonth={prevCalMonth}
            onNextMonth={nextCalMonth}
            onSelect={setDetailInvoice}
          />
        )
      )}


      <InvoiceDetailModal
        invoice={detailInvoice}
        onClose={() => setDetailInvoice(null)}
        onPay={inv => setPayTarget(inv)}
        entities={entities}
      />

      <RecordSalePaymentModal invoice={payTarget} onClose={() => setPayTarget(null)} entityName={payTarget ? entityName(payTarget.toEntityId) : undefined} />
    </AppLayout>
  );
}
